import { useNavigate } from "react-router";
import { ReactComponent as EditIcon } from "../../assets/icons/tables/edit-icon.svg";
import { ReactComponent as TrashIcon } from "../../assets/icons/tables/trash-icon.svg";
import { IPartner, usePartners } from "../../store/usePartners";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useMemo } from "react";

function DragHandle(props: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      {...props}
      className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 select-none px-1"
      title="Drag to reorder"
    >
      ⠿
    </div>
  );
}

function SortableRow({
  partner,
  onEdit,
  onDelete,
}: {
  partner: IPartner;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: partner._id as string });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className="bg-white hover:bg-gray-100 transition ease-linear border-b"
    >
      <td className="px-2 py-1 sm:py-4 w-8">
        <DragHandle {...attributes} {...listeners} />
      </td>
      <td
        onClick={onEdit}
        className="px-2 sm:px-6 py-1 sm:py-4 whitespace-nowrap cursor-pointer"
      >
        {partner.name}
        {partner.isCourier ? (
          <span className="ml-2 rounded-full bg-droppie-oranje px-2.5 py-0.5 text-xs font-semibold text-white">
            Carrier
          </span>
        ) : null}
      </td>
      <td
        onClick={onEdit}
        className="px-2 sm:px-6 py-1 sm:py-4 cursor-pointer"
      >
        {partner?.website}
      </td>
      <td
        onClick={onEdit}
        className={`px-2 sm:px-6 py-1 sm:py-4 cursor-pointer ${
          partner?.showInApp ? "text-green-700" : "text-red-700"
        }`}
      >
        {partner?.showInApp ? "Yes" : "No"}
      </td>
      <td className="px-2 sm:px-6 py-1 sm:py-4 flex items-center justify-end cursor-pointer">
        <EditIcon onClick={onEdit} className="mr-7" />
        {/* A carrier is deleted from its own page: deleting it from here
            would also stop parcel registration for it. */}
        {partner.isCourier ? null : <TrashIcon onClick={onDelete} />}
      </td>
    </tr>
  );
}

export const PartnersTable = ({
  carriersOnly = false,
}: {
  /** Parcel carriers instead of ordinary partners. A carrier is not in the
   *  app's partner list unless an admin shows it, and a shown one is ordered
   *  on the Partners page, so this mode drops the visible / not visible split
   *  and the reordering that goes with it. */
  carriersOnly?: boolean;
}) => {
  const { allPartners, deletePartner, reorderPartners } = usePartners();
  const navigate = useNavigate();

  const sensors = useSensors(useSensor(PointerSensor));

  const { visiblePartners, hiddenPartners, carrierPartners } = useMemo(() => {
    const sorted = [...allPartners].sort(
      (a, b) => (a.order ?? 0) - (b.order ?? 0)
    );
    // Carriers have their own page. Listing them here under "Not visible in
    // app" read as a misconfiguration, when it is the rule for a carrier.
    const ordinary = sorted.filter((p) => !p.isCourier);
    return {
      // The same list the app shows, in the same order: a carrier an admin
      // has shown is listed and dragged with the others (the app drops a
      // deactivated one, so this list does too).
      visiblePartners: sorted.filter(
        (p) => p.showInApp && (!p.isCourier || p.active !== false)
      ),
      hiddenPartners: ordinary.filter((p) => !p.showInApp),
      carrierPartners: sorted.filter((p) => p.isCourier),
    };
  }, [allPartners]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = visiblePartners.findIndex((p) => p._id === active.id);
    const newIndex = visiblePartners.findIndex((p) => p._id === over.id);
    const reordered = arrayMove(visiblePartners, oldIndex, newIndex);

    reorderPartners(
      reordered.map((p, i) => ({ id: p._id as string, order: i }))
    );
  };

  const tableHead = (
    <thead className="font-medium bg-gray-50">
      <tr>
        <th className="px-2 py-1 sm:py-4 w-8" />
        <th scope="col" className="px-2 sm:px-6 py-1 sm:py-4">Name</th>
        <th scope="col" className="px-2 sm:px-6 py-1 sm:py-4">Website</th>
        <th scope="col" className="px-2 sm:px-6 py-1 sm:py-4">Visible in app</th>
        <th scope="col" className="px-2 sm:px-6 py-1 sm:py-4 text-right">Actions</th>
      </tr>
    </thead>
  );

  const openPartner = (partnerId?: string) =>
    navigate(`/edit-partner/${partnerId}`);
  const confirmDelete = (partnerId?: string) => {
    if (window.confirm("Are you sure you want to delete this Partner?")) {
      deletePartner(partnerId as string);
    }
  };

  if (carriersOnly) {
    return (
      <div className="relative overflow-x-auto">
        <table className="w-full text-base font-normal font-satoshi text-left rtl:text-right text-dark">
          <thead className="font-medium bg-gray-50">
            <tr>
              <th scope="col" className="px-2 sm:px-6 py-1 sm:py-4">Name</th>
              <th scope="col" className="px-2 sm:px-6 py-1 sm:py-4">Website</th>
              <th scope="col" className="px-2 sm:px-6 py-1 sm:py-4">Visible in app</th>
              <th scope="col" className="px-2 sm:px-6 py-1 sm:py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {carrierPartners.map((partner) => (
              <tr
                key={partner._id}
                className="bg-white hover:bg-gray-100 transition ease-linear border-b"
              >
                <td
                  onClick={() => openPartner(partner._id)}
                  className="px-2 sm:px-6 py-1 sm:py-4 whitespace-nowrap cursor-pointer"
                >
                  {partner.name}
                </td>
                <td
                  onClick={() => openPartner(partner._id)}
                  className="px-2 sm:px-6 py-1 sm:py-4 cursor-pointer"
                >
                  {partner?.website}
                </td>
                <td
                  onClick={() => openPartner(partner._id)}
                  className={`px-2 sm:px-6 py-1 sm:py-4 cursor-pointer ${
                    partner?.showInApp && partner?.active !== false
                      ? "text-green-700"
                      : "text-gray-500"
                  }`}
                >
                  {partner?.showInApp && partner?.active !== false ? "Yes" : "No"}
                </td>
                <td className="px-2 sm:px-6 py-1 sm:py-4 flex items-center justify-end cursor-pointer">
                  <EditIcon
                    onClick={() => openPartner(partner._id)}
                    className="mr-7"
                  />
                  <TrashIcon onClick={() => confirmDelete(partner._id)} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="relative overflow-x-auto">
      <table className="w-full text-base font-normal font-satoshi text-left rtl:text-right text-dark">
        {tableHead}
        <tbody>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={visiblePartners.map((p) => p._id as string)}
              strategy={verticalListSortingStrategy}
            >
              {visiblePartners.map((partner) => (
                <SortableRow
                  key={partner._id}
                  partner={partner}
                  onEdit={() => navigate(`/edit-partner/${partner._id}`)}
                  onDelete={() => {
                    if (window.confirm("Are you sure you want to delete this Partner?")) {
                      deletePartner(partner._id as string);
                    }
                  }}
                />
              ))}
            </SortableContext>
          </DndContext>

          {hiddenPartners.length > 0 && (
            <>
              <tr>
                <td
                  colSpan={5}
                  className="px-2 sm:px-6 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider bg-gray-50 border-b"
                >
                  Not visible in app
                </td>
              </tr>
              {hiddenPartners.map((partner) => (
                <tr
                  key={partner._id}
                  className="bg-white hover:bg-gray-100 transition ease-linear border-b opacity-60"
                >
                  <td className="px-2 py-1 sm:py-4 w-8" />
                  <td
                    onClick={() => navigate(`/edit-partner/${partner._id}`)}
                    className="px-2 sm:px-6 py-1 sm:py-4 whitespace-nowrap cursor-pointer"
                  >
                    {partner.name}
                  </td>
                  <td
                    onClick={() => navigate(`/edit-partner/${partner._id}`)}
                    className="px-2 sm:px-6 py-1 sm:py-4 cursor-pointer"
                  >
                    {partner?.website}
                  </td>
                  <td
                    onClick={() => navigate(`/edit-partner/${partner._id}`)}
                    className="px-2 sm:px-6 py-1 sm:py-4 text-red-700 cursor-pointer"
                  >
                    No
                  </td>
                  <td className="px-2 sm:px-6 py-1 sm:py-4 flex items-center justify-end cursor-pointer">
                    <EditIcon
                      onClick={() => navigate(`/edit-partner/${partner._id}`)}
                      className="mr-7"
                    />
                    <TrashIcon
                      onClick={() => {
                        if (window.confirm("Are you sure you want to delete this Partner?")) {
                          deletePartner(partner._id as string);
                        }
                      }}
                    />
                  </td>
                </tr>
              ))}
            </>
          )}
        </tbody>
      </table>
    </div>
  );
};
